import { type ChipProps } from "./chip";
import { getColorVariable } from "@components/common/utils/helper";
import { KeyValueObject } from "@components/common/types/general";

export function getBackgroundColor(
    color: ChipProps["color"],
    fullTone: ChipProps["fullTone"],
    outline: ChipProps["outline"],
    theme: any,
): KeyValueObject | null {
    if (outline) {
        return null;
    }
    if (fullTone) {
        return {
            backgroundColor: getColorVariable(color, theme, {
                gray: theme.colors.gray[500],
                white: theme.colors.white,
                colorize: theme.colors[color || "primary"][500],
                default: theme.colors.gray[950],
            }),
        };
    }
    return {
        backgroundColor: getColorVariable(color, theme, {
            gray: theme.colors.gray[100],
            white: theme.colors.white,
            colorize: theme.colors[color || "primary"][200],
            default: theme.colors.gray[950],
        }),
    };
}

export function getColor(color: ChipProps["color"], fullTone: ChipProps["fullTone"], outline: ChipProps["outline"], theme: any): KeyValueObject {
    if (outline) {
        return {
            color: getColorVariable(color, theme, {
                gray: theme.colors.gray[500],
                white: theme.colors.gray[500],
                colorize: theme.colors[color || "primary"][500],
                default: theme.colors.gray[950],
            }),
        };
    }
    if (fullTone) {
        return {
            color: getColorVariable(color, theme, {
                gray: theme.colors.white,
                white: theme.colors.gray[950],
                colorize: theme.colors.white,
                default: theme.colors.white,
            }),
        };
    }
    return {
        color: getColorVariable(color, theme, {
            gray: theme.colors.gray[800],
            white: theme.colors.gray[800],
            colorize: theme.colors[color || "primary"][800],
            default: theme.colors.white,
        }),
    };
}

export function getBorderColor(color: ChipProps["color"], fullTone: ChipProps["fullTone"], theme: any): KeyValueObject {
    if (fullTone) {
        return {
            borderColor: getColorVariable(color, theme, {
                gray: theme.colors.gray[600],
                white: theme.colors.white,
                colorize: theme.colors[color || "primary"][600],
                default: theme.colors.gray[950],
            }),
        };
    }
    return {
        borderColor: getColorVariable(color, theme, {
            gray: theme.colors.gray[200],
            white: theme.colors.gray[200],
            colorize: theme.colors[color || "primary"][300],
            default: theme.colors.gray[950],
        }),
    };
}

export function getFontSize(size: ChipProps["size"], theme: any): KeyValueObject {
    switch (size) {
        case "xs":
            return { fontSize: theme.fontSize["3xs"] };
        case "sm":
            return { fontSize: theme.fontSize["3xs"] };
        case "lg":
            return { fontSize: theme.fontSize.xs };
        case "xl":
            return { fontSize: theme.fontSize.sm };
        default:
            return { fontSize: theme.fontSize["2xs"] };
    }
}

export function getLineHeight(size: ChipProps["size"]): KeyValueObject {
    switch (size) {
        case "xs":
            return { lineHeight: "16px" };
        case "sm":
            return { lineHeight: "18px" };
        case "lg":
            return { lineHeight: "24px" };
        case "xl":
            return { lineHeight: "28px" };
        default:
            return { lineHeight: "20px" };
    }
}

export function getPadding(size: ChipProps["size"], label: ChipProps["label"], theme: any): KeyValueObject {
    if (!label) {
        switch (size) {
            case "xs":
                return {
                    paddingLeft: "3px",
                    paddingRight: "3px",
                };
            case "sm":
                return {
                    paddingLeft: theme.spacing[1],
                    paddingRight: theme.spacing[1],
                };
            case "lg":
                return {
                    paddingLeft: "5px",
                    paddingRight: "5px",
                };
            case "xl":
                return {
                    paddingLeft: theme.spacing[1.5],
                    paddingRight: theme.spacing[1.5],
                };
            default:
                return {
                    paddingLeft: theme.spacing[1],
                    paddingRight: theme.spacing[1],
                };
        }
    }
    switch (size) {
        case "xs":
            return {
                paddingLeft: theme.spacing[1.5],
                paddingRight: theme.spacing[1.5],
            };
        case "sm":
            return {
                paddingLeft: theme.spacing[2],
                paddingRight: theme.spacing[2],
            };
        case "lg":
            return {
                paddingLeft: theme.spacing[3],
                paddingRight: theme.spacing[3],
            };
        case "xl":
            return {
                paddingLeft: theme.spacing[4],
                paddingRight: theme.spacing[4],
            };
        default:
            return {
                paddingLeft: theme.spacing[2.5],
                paddingRight: theme.spacing[2.5],
            };
    }
}

export function getBorderRadius(rounded: ChipProps["rounded"], theme: any): KeyValueObject | null {
    switch (rounded) {
        case "md":
            return { borderRadius: theme.borderRadius.DEFAULT };
        case "lg":
            return { borderRadius: theme.borderRadius.lg };
        case "full":
            return { borderRadius: theme.borderRadius.full };
        default:
            return null;
    }
}

export function getAdornmentsHeight(size: ChipProps["size"], theme: any): KeyValueObject {
    switch (size) {
        case "xs":
            return { height: theme.spacing[4] };
        case "sm":
            return { height: "18px" };
        case "lg":
            return { height: theme.spacing[6] };
        case "xl":
            return { height: theme.spacing[7] };
        default:
            return { height: theme.spacing[5] };
    }
}

export function getAdornmentsSvgSize(size: ChipProps["size"], theme: any): KeyValueObject {
    switch (size) {
        case "xs":
        case "sm":
            return { width: theme.spacing[2.5], height: theme.spacing[2.5] };
        case "lg":
            return { width: theme.spacing[3.5], height: theme.spacing[3.5] };
        case "xl":
            return { width: theme.spacing[4], height: theme.spacing[4] };
        default:
            return { width: theme.spacing[3], height: theme.spacing[3] };
    }
}
